import { ChangeDetectorRef, Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Subscription, firstValueFrom, forkJoin, of } from 'rxjs';
import { ChqWidgetsButtonModel } from 'src/app/model/chq-widgets-button-model';
import { CommonService } from 'src/app/services/common/common.service';
import { MonitorService } from 'src/app/services/monitor/monitor.service';
import { ThirdPartyCommonLayoutComponent } from '../common-layout/common-layout.component';
import { ClaimsService } from 'src/app/services/claims/claims.service';
import { SeverityLevel } from '@microsoft/applicationinsights-web';
import { HttpHeaders } from '@angular/common/http';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { InspectionService } from 'src/app/services/inspection/inspection.service';
import { MediaCollectionService } from 'src/app/services/media-collection/media-collection.service';
import dayjs from 'dayjs';
import { ChqUploadComponent } from 'src/app/widgets/chq-upload/chq-upload.component';
import { uploadEventResult } from 'src/app/model/chq-upload-model';

@Component({
  selector: 'app-registered-garage-assignment-action',
  templateUrl: './registered-garage-assignment-action.component.html',
  styleUrls: [ './registered-garage-assignment-action.component.scss' ],
  standalone: false
})
export class RegisteredGarageAssignmentActionComponent implements OnInit, OnDestroy {
  @ViewChild(ThirdPartyCommonLayoutComponent) commonLayout: ThirdPartyCommonLayoutComponent;
  @ViewChild('chqUpload') chqUpload: ChqUploadComponent;
  @ViewChild('xaUpload') xaUpload: ChqUploadComponent;
  private token: string = '';
  public claimGuid: string = '';
  public claimDetails: any = {};
  public isSubmissionSuccess: boolean = false;
  subscriptions: Subscription[] = [];
  public viewRepairDetails: ChqWidgetsButtonModel = {
    label: 'view_repair_details',
    type: 'outline',
    icon: 'report-check',
    onclick: this.handleViewRepairDetails.bind(this)
  };
  assessmentRequestForm: FormGroup;

  domainId: number;
  orgId: number;
  accountId: number;
  tenantId: number;
  currencyName: string;
  mediaDetail: any;
  surveyMedia: any;
  collectionId: number;
  collectionGuid: string;
  totalImageCount: number = 20;
  uploadedImageCount: number = 0;
  bulkUploadInspectionItem: any;
  spiInspectionItems: Array<any> = [];
  imageFiles: Array<any> = [];
  isBulkUploadMethod: boolean = true;

  /**
   * constructor
   */
  constructor(
    public commonService: CommonService,
    private monitorService: MonitorService,
    private route: ActivatedRoute,
    private claimsService: ClaimsService,
    private readonly _fb: FormBuilder,
    private readonly translationService: TranslateService,
    private readonly inspectionService: InspectionService,
    private readonly mediaCollectionService: MediaCollectionService,
    private readonly cdr: ChangeDetectorRef
  ) {
    this.token = this.route.snapshot.queryParams['token'];
    const obj: any = atob(this.token);
    const parsedObj = JSON.parse(obj);
    this.claimGuid = parsedObj.ClaimGuid;
    this.assessmentRequestForm = this._fb.group({
      partsTotalCost: [ '', [ Validators.required ] ],
      labourTotalCost: [ '', [ Validators.required ] ],
      grandTotal: [ '0' ],
      notes: [ '' ]
    });

    this.assessmentRequestForm.get('partsTotalCost').valueChanges.subscribe((value) => {
      const partTotalCost = value || 0;
      const labourTotalCost = this.assessmentRequestForm.get('labourTotalCost').value || 0;
      this.assessmentRequestForm.get('grandTotal').patchValue(partTotalCost + labourTotalCost);
    });

    this.assessmentRequestForm.get('labourTotalCost').valueChanges.subscribe((value) => {
      const labourTotalCost = value || 0;
      const partTotalCost = this.assessmentRequestForm.get('partsTotalCost').value || 0;
      this.assessmentRequestForm.get('grandTotal').patchValue(labourTotalCost + partTotalCost);
    });
  }

  /**
   * on init
   */
  ngOnInit(): void {
    this.commonService.dismissBootstrapLoader();
    this.monitorService.logEvent('ngOnInit', [ 'RegisteredGarageAssignmentActionComponent', 'addenda-claim' ]);
    const status = localStorage.getItem(this.token);
    if (status === 'submitted') {
      this.isSubmissionSuccess = true;
      return;
    }

    const claimDetails$ = this.commonService.thirdPartyClaimDetails.subscribe({
      next: (response) => {
        this.claimDetails = response;
        this.domainId = this.claimDetails?.domainId;
        this.orgId = this.claimDetails?.orgId;
        this.accountId = this.claimDetails?.accountID;
        this.tenantId = this.claimDetails?.tenantID;
        this.currencyName = this.claimDetails?.organizationDetail?.currencyName;

        if (this.claimDetails?.customerDetails) {
          this.claimDetails.customerDetails.customerFullName = '';
          if (this.claimDetails.customerDetails.policyHolderFirstName) {
            this.claimDetails.customerDetails.customerFullName = this.claimDetails.customerDetails.policyHolderFirstName;
          }
          if (this.claimDetails.customerDetails.policyHolderLastName) {
            this.claimDetails.customerDetails.customerFullName += ` ${this.claimDetails.customerDetails.policyHolderLastName}`;
          }
        }
        if (this.claimDetails?.garageDetails?.estimateAmount) {
          this.isSubmissionSuccess = true;
          return;
        }
        if (this.domainId) {
          this.getTemplate();
        }

        this.cdr.markForCheck();
      }
    });

    this.subscriptions.push(claimDetails$);
  }

  /**
   * on destroy
   */
  ngOnDestroy(): void {
    this.monitorService.logEvent('ngOnDestroy', [ 'RegisteredGarageAssignmentActionComponent', 'addenda-claim' ]);
    if (this.subscriptions) {
      this.subscriptions.forEach((subscription: Subscription) => subscription.unsubscribe());
    }
  }

  /**
   * handleViewRepairDetails
   */
  handleViewRepairDetails(): void {
    this.monitorService.logEvent('handleViewRepairDetails', [ 'RegisteredGarageAssignmentActionComponent', 'addenda-claim' ]);
    this.commonLayout.handleViewRepairDetails();
  }

  /**
   * handleViewRepairDetails
   */
  /**
   * handleViewRepairDetails
   */
  handleFormSubmit(): void {
    const headers = new HttpHeaders().set('Authorization', `Bearer ${this.commonLayout.idToken}`);
    this.commonService.showLoading();
    const date: any = new Date();
    const formData = new FormData();
    formData.append('ClaimGuid', this.claimGuid);
    formData.append('TotalAmount', this.assessmentRequestForm.get('grandTotal').value);
    formData.append('StartDate', dayjs(date).format('YYYY-MM-DD'));
    formData.append('ExcessPayable', '0');
    formData.append('Description', this.assessmentRequestForm.get('notes').value);
    formData.append('Currency', this.currencyName);
    formData.append('PartAmount', this.assessmentRequestForm.get('partsTotalCost').value);
    formData.append('LaborAmount', this.assessmentRequestForm.get('labourTotalCost').value);

    firstValueFrom(this.claimsService.submitRegisteredGarageEstimate(formData, headers))
      .then((response) => {
        this.commonService.hideLoading();
        if (response) {
          this.isSubmissionSuccess = true;
          localStorage.setItem(this.token, 'submitted');
          this.cdr.detectChanges();
        }
      })
      .catch((err) => {
        this.commonService.hideLoading();
        this.monitorService.logException(err, SeverityLevel.Error);
      });
  }

  /**
   * get document/ image details
   */
  getTemplate(): void {
    this.commonService.showLoading();
    forkJoin({
      mediaTemplate: this.mediaCollectionService.getMediaTemplate(this.domainId, this.orgId, this.accountId, this.tenantId),
      inspectionTemplate: this.inspectionService.getInspectionTemplateList(this.domainId, this.orgId, this.accountId, this.tenantId)
    }).subscribe({
      next: (res) => {
        this.commonService.hideLoading();
        const template = res.mediaTemplate.data[0];
        this.collectionId = template?.id;
        this.collectionGuid = template?.collectionGuid;
        const inspectionTemplate = res.inspectionTemplate?.data;
        let inspectionTemplateId = 0;
        if (inspectionTemplate?.length > 0) {
          inspectionTemplateId = inspectionTemplate[0].id;
        }
        this.getTemplateDetail(this.collectionId, inspectionTemplateId);

        this.cdr.markForCheck();
      },
      error: (error) => {
        this.commonService.hideLoading();
        this.monitorService.logException(error, SeverityLevel.Error);
      }
    });
  }

  /**
   * get template detail
   */
  getTemplateDetail(collectionId: number, inspectionTemplateId: number): void {
    this.commonService.showLoading();
    forkJoin({
      mediaDetail: collectionId
        ? this.mediaCollectionService.getMediaDocumentData(
            collectionId,
            this.claimDetails.id,
            this.domainId,
            this.orgId,
            this.accountId,
            this.tenantId
          )
        : of(null),
      inspectionTemplateDetail: this.inspectionService.getInspectionTemplate(inspectionTemplateId),
      inspectionDetail: this.inspectionService.getInspection(this.claimDetails.inspectionId, this.orgId, this.accountId, this.tenantId)
    }).subscribe({
      next: (response) => {
        this.commonService.hideLoading();
        this.mediaDetail = response.mediaDetail?.data?.medias || [];
        this.surveyMedia = this.mediaDetail.find(x => x.name?.toLowerCase() === 'survey report') || ({} as any);
        this.surveyMedia = {
          ...this.surveyMedia,
          allowExtension: this.surveyMedia?.mediaParts?.[0]?.allowExtension
        };
        const inspectionItems = response.inspectionDetail?.inspectionItems || [];
        this.spiInspectionItems = inspectionItems.filter(x => x.procedureSteps !== 'Bulk Upload Image');
        const bulkUploadInspectionItems = inspectionItems.filter(x => x.procedureSteps === 'Bulk Upload Image');
        const bulkUploadedItem = bulkUploadInspectionItems.filter(x => x.rawAzureBlobUrl);
        const bulkUploadTemplateItem = response.inspectionTemplateDetail?.inspectionItemTemplates?.find(
          x => x.name?.toLowerCase() === 'bulk upload image'
        );
        this.bulkUploadInspectionItem = bulkUploadTemplateItem;
        const spiUploadedItem = this.spiInspectionItems.filter(x => x.rawAzureBlobUrl);
        if (spiUploadedItem?.length > 0) {
          this.isBulkUploadMethod = false;
        }
        const uploadedCount = this.isBulkUploadMethod ? bulkUploadedItem?.length : spiUploadedItem?.length;
        this.totalImageCount = this.totalImageCount - uploadedCount;

        this.cdr.markForCheck();
      },
      error: (error) => {
        this.commonService.hideLoading();
        this.monitorService.logException(error, SeverityLevel.Error);
      }
    });
  }

  /**
   * upload document
   */
  uploadDocument(): void {
    this.chqUpload.triggerUpload();
  }

  /**
   * handle image output
   * @param $event
   */
  handleDocumentOutput($event: uploadEventResult | uploadEventResult[]): void {
    const { file } = $event as uploadEventResult;
    const obj: any = {
      mediaCollectionId: this.collectionGuid,
      domainId: this.domainId,
      objectId: this.claimDetails.id,
      mediaTemplateId: this.surveyMedia.mediaTemplateGuid,
      mediaPartTemplateId: this.surveyMedia.mediaParts?.[0]?.mediaPartTemplateGuid,
      mediaGuid: this.surveyMedia.mediaGuid,
      mediaPartGuid: this.surveyMedia.mediaParts?.[0]?.mediaPartGuid
    };

    this.surveyMedia.uploadInProgress = true;
    let observer: any = null;
    if (obj.mediaPartGuid && obj.mediaPartGuid != '00000000-0000-0000-0000-000000000000') {
      observer = this.mediaCollectionService.updateDocument(file, obj, this.orgId, this.accountId, this.tenantId);
    } else {
      observer = this.mediaCollectionService.uploadDocument(file, obj, this.orgId, this.accountId, this.tenantId);
    }
    this.surveyMedia.isUploadSuccess = false;
    observer.subscribe({
      next: (res: any): void => {
        if (res) {
          this.surveyMedia.mediaGuid = res.data?.mediaGuid;
          this.surveyMedia.mediaPartGuid = res.data?.mediaPartGuid;
          this.surveyMedia.getURL = res.data?.blobUrl;
          this.surveyMedia.fileName = file.name;
          this.surveyMedia.contentType = file.type;
          this.surveyMedia.fileSize = file.size;
          this.surveyMedia.isUploadSuccess = true;
          this.surveyMedia.uploadInProgress = false;
          const mediaPart = this.surveyMedia.mediaParts?.[0];
          mediaPart.mediaGuid = this.surveyMedia.mediaGuid;
          mediaPart.mediaPartGuid = this.surveyMedia.mediaPartGuid;
          mediaPart.getURL = this.surveyMedia.getURL;
          mediaPart.fileName = this.surveyMedia.fileName;
          mediaPart.contentType = this.surveyMedia.contentType;
          mediaPart.fileSize = this.surveyMedia.fileSize;
          mediaPart.isUploadSuccess = this.surveyMedia.isUploadSuccess;
        }
      },
      error: (): void => {
        this.surveyMedia.isUploadSuccess = false;
        this.surveyMedia.uploadInProgress = false;
      },
      complete: (): void => this.commonService.hideLoading()
    });
  }

  /**
   * handles image upload
   * @param event
   */
  handleImageOutput($event: any): void {
    const events = Array.isArray($event) ? $event : $event.image ? [ $event ] : Object.values($event);
    let index = 0;
    const uploadedSteps = [];
    //remove item from events object if greater than remaining slots
    const remainingSlots = this.totalImageCount - this.imageFiles.length;
    if (events.length > remainingSlots) {
      events.splice(remainingSlots);
    }

    for (const i in events) {
      const fileResult = events[i] as any;
      const stepObj: any = {
        stepName: fileResult.fileName,
        uploadStatus: 'notUploaded',
        url: '',
        image: fileResult.image,
        imageFile: fileResult.file,
        index,
        sizeLimit: 5242880,
        isMassUpload: true,
        allowExtension: [ 'jpg', 'jpeg', 'png' ],
        mandatory: true,
        placeHolderUrl: '',
        isUploadSuccess: false,
        uploadInProgress: false,
        isUploadFailed: false,
        isSkipped: false,
        isSkipEnabled: true,
        templateGuid: '',
        partTemplateGuid: ''
      };
      index++;
      uploadedSteps.push(stepObj);
      const img = new Image();
      img.src = fileResult.image as string;
      img.onload = (): void => {
        stepObj.uploadInProgress = true;
        stepObj.isSkipped = false;
        stepObj.isUploadFailed = false;
        stepObj.isUploadSuccess = false;
        stepObj.height = img.height;
        stepObj.width = img.width;

        this.imageFiles.push(stepObj);
        this.cdr.detectChanges();
        this.uploadImage(stepObj, fileResult.file);
      };
    }
  }

  /**
   * uploadImage
   * @param uploadObject
   * @param file
   */
  uploadImage(step: any, file: File): void {
    const templateId = this.isBulkUploadMethod
      ? this.bulkUploadInspectionItem?.id
      : this.spiInspectionItems.find(x => !x.rawAzureBlobUrl)?.inspectionItemTemplateID;
    const uploadObject = {
      height: step.height,
      width: step.width,
      inspectionId: this.claimDetails.inspectionId,
      templateId,
      type: '',
      isBulk: this.isBulkUploadMethod,
      objectId: this.claimDetails.id,
      domainId: this.claimDetails.domainId
    };
    const headers: Map<string, string> = new Map();
    if (this.orgId) {
      headers.set('orgId', this.orgId.toString());
    }
    if (this.accountId) {
      headers.set('accountId', this.accountId.toString());
    }
    if (this.tenantId) {
      headers.set('tenantId', this.tenantId.toString());
    }
    this.inspectionService.uploadImageBulk(uploadObject, file, false, 'body', headers).subscribe({
      next: (data: any) => {
        step.rawAzureBlobUrl = data.rawImage?.rawAzureBlobUrl;
        step.inspectionItem = data;
        step.procedureSteps = 'Bulk Upload Image';
        step.uploadInProgress = false;
        step.isUploadSuccess = true;
        this.uploadedImageCount++;
        this.cdr.markForCheck();
      },
      error: () => {
        step.uploadInProgress = false;
        step.isUploadSuccess = false;
        this.imageFiles = this.imageFiles.filter(x => x !== step);
        this.cdr.markForCheck();
      }
    });
  }

  /**
   * bulk upload
   */
  bulkUpload(): void {
    this.xaUpload.triggerUpload();
  }

  /**
   * confirm delete
   * @param index
   */
  confirmDelete(item: any): void {
    const actions = [
      {
        color: 'gray',
        type: 'outline',
        name: 'no'
      },
      {
        color: 'primary',
        type: 'default',
        name: 'yes'
      }
    ];
    this.commonService
      .openWarningDialog('delete_vehicle_photo', actions)
      .afterClosed()
      .subscribe((data) => {
        if (data.result && data.data.name === 'yes') {
          this.deleteImage(item);
        }
      });
  }

  /**
   *  delete image
   */
  deleteImage(step: any): void {
    this.commonService.showLoading();
    if (step.inspectionItem.id) {
      this.inspectionService.deleteInspection(step.inspectionItem.id).subscribe(
        () => {
          this.imageFiles = this.imageFiles.filter(x => x?.inspectionItem?.id !== step?.inspectionItem?.id);
          this.uploadedImageCount--;
          this.commonService.hideLoading();
          this.cdr.markForCheck();
        },
        () => {
          this.commonService.hideLoading();
        }
      );
    }
  }

  /** True while survey document or any vehicle photo upload is in flight. */
  get hasActiveUpload(): boolean {
    const documentUploading = !!this.surveyMedia?.uploadInProgress;
    const imageUploading = this.imageFiles?.some((x: any) => x?.uploadInProgress) ?? false;
    return documentUploading || imageUploading;
  }
}
